export const REPORT_DATA_SCOPE = {
  CURRENT_VIEW: 'current_view',
  ALL_DATA: 'all_data',
}

export const REPORT_DATA_SCOPE_LABELS = {
  [REPORT_DATA_SCOPE.CURRENT_VIEW]: 'Current view',
  [REPORT_DATA_SCOPE.ALL_DATA]: 'Include all data',
}

export const createDefaultWidgetDataScopes = (
  chartIds,
  scope = REPORT_DATA_SCOPE.CURRENT_VIEW
) => Object.fromEntries(chartIds.map((id) => [id, scope]))

export const buildExportDataScopePayload = ({
  reportDataScope,
  widgetDataScopes,
  draftExportCharts,
  allChartIds,
}) => ({
  reportDataScope,
  widgets: allChartIds.map((id) => ({
    id,
    included: draftExportCharts.has(id),
    dataScope: widgetDataScopes[id] || reportDataScope,
  })),
})

export const REPORT_DATA_SCOPE_SHORT_LABELS = {
  [REPORT_DATA_SCOPE.CURRENT_VIEW]: 'Current view',
  [REPORT_DATA_SCOPE.ALL_DATA]: 'All data',
}

export const formatDataScopeLabel = (scope) =>
  REPORT_DATA_SCOPE_LABELS[scope] || REPORT_DATA_SCOPE_LABELS[REPORT_DATA_SCOPE.CURRENT_VIEW]

export const getOppositeDataScope = (scope) =>
  scope === REPORT_DATA_SCOPE.CURRENT_VIEW
    ? REPORT_DATA_SCOPE.ALL_DATA
    : REPORT_DATA_SCOPE.CURRENT_VIEW

export const getWidgetScopeToggleTooltip = ({ isOverride, reportDataScope }) => {
  if (reportDataScope === REPORT_DATA_SCOPE.CURRENT_VIEW) {
    return isOverride
      ? 'Turn off to use current dashboard view for this chart'
      : 'Turn on to use all data for this chart'
  }

  return isOverride
    ? 'Turn off to use all data for this chart'
    : 'Turn on to use current dashboard view for this chart'
}

export const PAID_MEDIA_EXPORT_CHART_IDS = [
  'paidMediaPerformance',
  'topPerformingAds',
  'topObjectives',
  'topCampaigns',
  'advertisingPerformance',
]

export const PAID_MEDIA_WIDGET_VIEW_BY_CHART_ID = {
  topPerformingAds: 'ads',
  topObjectives: 'objectives',
  topCampaigns: 'campaigns',
  advertisingPerformance: 'performance',
}

export function resolveChartExportDataScope(chartId, reportDataScope, widgetDataScopes = {}) {
  return widgetDataScopes[chartId] ?? reportDataScope ?? REPORT_DATA_SCOPE.CURRENT_VIEW
}

export function chartExportUsesAllData(chartId, reportDataScope, widgetDataScopes = {}) {
  return resolveChartExportDataScope(chartId, reportDataScope, widgetDataScopes) === REPORT_DATA_SCOPE.ALL_DATA
}

export function shouldUseAllDashboardFiltersForExport(reportDataScope, widgetDataScopes = {}) {
  if (reportDataScope === REPORT_DATA_SCOPE.ALL_DATA) return true
  return Object.values(widgetDataScopes).some((scope) => scope === REPORT_DATA_SCOPE.ALL_DATA)
}

export function expandPaidMediaChartIdsForAllData(
  chartIds,
  { reportDataScope, widgetDataScopes, enabledPaidChartIds },
) {
  const set = new Set(chartIds)
  const hasPaid = [...set].some((id) => PAID_MEDIA_EXPORT_CHART_IDS.includes(id))
  if (!hasPaid) return [...set]

  const wantsAllPaid =
    reportDataScope === REPORT_DATA_SCOPE.ALL_DATA ||
    [...set].some(
      (id) =>
        PAID_MEDIA_EXPORT_CHART_IDS.includes(id) &&
        chartExportUsesAllData(id, reportDataScope, widgetDataScopes),
    )

  if (!wantsAllPaid) return [...set]

  enabledPaidChartIds.forEach((id) => {
    if (PAID_MEDIA_EXPORT_CHART_IDS.includes(id)) set.add(id)
  })
  return [...set]
}

const PAID_ADS_EXPORT_ALIAS_IDS = new Set([
  'topPaidAds',
  'advertisingPerformance',
  'topObjectives',
  'topCampaigns',
])

/** Collapse duplicate paid-ads chart ids into one Top Performing Ads export. */
export function dedupeExportChartIds(chartIds, { keepPaidMediaTabs = false } = {}) {
  const list = [...chartIds]
  const wantsPaidAds = list.some(
    (id) => id === 'topPerformingAds' || PAID_ADS_EXPORT_ALIAS_IDS.has(id),
  )
  const set = new Set(list)
  if (!keepPaidMediaTabs) {
    PAID_ADS_EXPORT_ALIAS_IDS.forEach((id) => set.delete(id))
    if (wantsPaidAds) set.add('topPerformingAds')
  }
  if (set.has('goalPerformance')) set.delete('socialGoalsOverview')
  return [...set]
}
